<script setup>
import { ref, onMounted } from 'vue';

// API-Basis-URL (für Produktion anpassen)
const API_URL = '/api';

// Musik-Daten
const tracks = ref([]);
const isLoading = ref(false);
const error = ref(null);
const successMessage = ref('');

// Musik-Kategorien und Streaming-Anbieter-Einstellungen
const musicSettings = ref({
  traditional_title: 'Traditionell',
  traditional_description: 'Klassische irische Balladen und Folk-Songs, authentisch interpretiert.',
  modern_title: 'Modern',
  modern_description: 'Zeitgenössische Musik mit irischem Flair und einzigartigen Arrangements.',
  live_title: 'Live',
  live_description: 'Energiegeladene Auftritte, die das Publikum zum Tanzen und Mitsingen bringen.',
  spotify_url: '',
  apple_music_url: '',
  youtube_music_url: ''
});

// Aktiven Tab setzen (tracks oder settings)
const activeTab = ref('tracks');

const newTrack = ref({
  title: '',
  album: '',
  year: '',
  description: '',
  spotifyLink: '',
  youtubeLink: '',
  appleMusicLink: '',
  isPublished: true
});

const editingTrack = ref(null);
const showForm = ref(false);

// Alle Tracks vom Server laden
const fetchTracks = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`${API_URL}/admin_music.php`);
    
    if (!response.ok) {
      throw new Error(`Fehler beim Laden der Daten: ${response.status}`);
    }
    
    const data = await response.json();
    tracks.value = data.tracks || [];
    
  } catch (err) {
    console.error('Fehler beim Laden der Musiktitel:', err);
    error.value = 'Musiktitel konnten nicht geladen werden. ' + (err.message || '');
    
    // Fallback für Entwicklung/Tests
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        tracks.value = [
          {
            id: 1,
            title: 'Irish Rover',
            album: 'Celtic Memories',
            year: '2023',
            description: 'Traditionelle irische Ballade über ein legendäres Schiff.',
            spotifyLink: 'https://open.spotify.com/track/example1',
            youtubeLink: 'https://youtube.com/watch?v=example1',
            appleMusicLink: 'https://music.apple.com/album/example1',
            isPublished: true
          },
          {
            id: 2,
            title: 'Whiskey in the Jar',
            album: 'Celtic Memories',
            year: '2023',
            description: 'Eines der bekanntesten irischen Volkslieder.',
            spotifyLink: 'https://open.spotify.com/track/example2',
            youtubeLink: 'https://youtube.com/watch?v=example2',
            appleMusicLink: 'https://music.apple.com/album/example2',
            isPublished: true
          },
          {
            id: 3,
            title: 'The Foggy Dew',
            album: 'Unpublished',
            year: '2024',
            description: 'Ballade über den Osteraufstand in Irland 1916.',
            spotifyLink: '',
            youtubeLink: 'https://youtube.com/watch?v=example3',
            appleMusicLink: '',
            isPublished: false
          }
        ];
      }, 500);
    }
  } finally {
    isLoading.value = false;
  }
};

// Musik-Einstellungen laden
const fetchMusicSettings = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`${API_URL}/music_settings.php`);
    
    if (!response.ok) {
      throw new Error(`Fehler beim Laden der Einstellungen: ${response.status}`);
    }
    
    const data = await response.json();
    if (data.success && data.settings) {
      musicSettings.value = {
        ...musicSettings.value,
        ...data.settings
      };
    }
  } catch (err) {
    console.error('Fehler beim Laden der Musik-Einstellungen:', err);
    error.value = 'Musik-Einstellungen konnten nicht geladen werden. ' + (err.message || '');
  } finally {
    isLoading.value = false;
  }
};

// Musik-Einstellungen speichern
const saveMusicSettings = async () => {
  isLoading.value = true;
  error.value = null;
  successMessage.value = '';
  
  try {
    const response = await fetch(`${API_URL}/music_settings.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(musicSettings.value)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Fehler beim Speichern der Einstellungen: ${response.status}`);
    }
    
    // Erfolgreiche Speicherung
    successMessage.value = 'Musik-Einstellungen wurden erfolgreich gespeichert.';
    setTimeout(() => {
      successMessage.value = '';
    }, 3000);
    
  } catch (err) {
    console.error('Fehler beim Speichern der Einstellungen:', err);
    error.value = 'Einstellungen konnten nicht gespeichert werden. ' + (err.message || '');
  } finally {
    isLoading.value = false;
  }
};

// Zeige Formular zum Hinzufügen eines neuen Tracks
const addNewTrack = () => {
  editingTrack.value = null;
  newTrack.value = {
    title: '',
    album: '',
    year: '',
    description: '',
    spotifyLink: '',
    youtubeLink: '',
    appleMusicLink: '',
    isPublished: true
  };
  showForm.value = true;
};

// Zeige Formular zum Bearbeiten eines bestehenden Tracks
const editTrack = (track) => {
  editingTrack.value = track.id;
  newTrack.value = { ...track };
  showForm.value = true;
};

// Speichern eines neuen oder bearbeiteten Tracks
const saveTrack = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`${API_URL}/music.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newTrack.value)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Fehler beim Speichern: ${response.status}`);
    }
    
    // Erfolgreiche Speicherung
    await fetchTracks(); // Daten neu laden
    showForm.value = false;
    editingTrack.value = null;
    
  } catch (err) {
    console.error('Fehler beim Speichern:', err);
    error.value = 'Track konnte nicht gespeichert werden. ' + (err.message || '');
    
    // Fallback für Entwicklung/Tests
    if (process.env.NODE_ENV === 'development') {
      if (editingTrack.value) {
        // Bestehenden Track aktualisieren
        const index = tracks.value.findIndex(t => t.id === editingTrack.value);
        if (index !== -1) {
          tracks.value[index] = { ...newTrack.value, id: editingTrack.value };
        }
      } else {
        // Neuen Track hinzufügen
        const newId = Math.max(0, ...tracks.value.map(t => t.id)) + 1;
        tracks.value.push({ ...newTrack.value, id: newId });
      }
      
      // Formular zurücksetzen
      showForm.value = false;
      editingTrack.value = null;
    }
  } finally {
    isLoading.value = false;
  }
};

// Track löschen
const deleteTrack = async (id) => {
  if (!confirm('Sind Sie sicher, dass Sie diesen Titel löschen möchten?')) {
    return;
  }
  
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`${API_URL}/music.php`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Fehler beim Löschen: ${response.status}`);
    }
    
    // Erfolgreiche Löschung
    tracks.value = tracks.value.filter(t => t.id !== id);
    
  } catch (err) {
    console.error('Fehler beim Löschen:', err);
    error.value = 'Track konnte nicht gelöscht werden. ' + (err.message || '');
    
    // Fallback für Entwicklung/Tests
    if (process.env.NODE_ENV === 'development') {
      tracks.value = tracks.value.filter(t => t.id !== id);
    }
  } finally {
    isLoading.value = false;
  }
};

// Veröffentlichungsstatus eines Tracks ändern
const togglePublishStatus = async (track) => {
  const updatedTrack = { 
    ...track, 
    isPublished: !track.isPublished 
  };
  
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`${API_URL}/music.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updatedTrack)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Fehler beim Aktualisieren: ${response.status}`);
    }
    
    // Erfolgreiche Aktualisierung
    const index = tracks.value.findIndex(t => t.id === track.id);
    if (index !== -1) {
      tracks.value[index] = updatedTrack;
    }
    
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Status:', err);
    error.value = 'Status konnte nicht aktualisiert werden. ' + (err.message || '');
    
    // Fallback für Entwicklung/Tests
    if (process.env.NODE_ENV === 'development') {
      const index = tracks.value.findIndex(t => t.id === track.id);
      if (index !== -1) {
        tracks.value[index] = updatedTrack;
      }
    }
  } finally {
    isLoading.value = false;
  }
};

// Abbrechen des Formulars
const cancelForm = () => {
  showForm.value = false;
  editingTrack.value = null;
  error.value = null;
};

// Komponente initialisieren
onMounted(() => {
  fetchTracks();
  fetchMusicSettings();
});
</script>

<template>
  <div class="music-manager">
    <div class="section-header">
      <h2>Musik verwalten</h2>
      <p class="section-description">
        Verwalten Sie hier Ihre Musik und zugehörige Einstellungen.
      </p>
    </div>
    
    <!-- Tab-Navigation -->
    <div class="tab-navigation">
      <button 
        :class="['tab-button', { active: activeTab === 'tracks' }]" 
        @click="activeTab = 'tracks'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18V5l12-2v13"></path>
          <circle cx="6" cy="18" r="3"></circle>
          <circle cx="21" cy="16" r="3"></circle>
        </svg>
        Musiktitel
      </button>
      <button 
        :class="['tab-button', { active: activeTab === 'settings' }]" 
        @click="activeTab = 'settings'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
        Einstellungen
      </button>
    </div>

    <!-- Erfolgsmeldung -->
    <div v-if="successMessage" class="success-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
    
    <!-- Fehlermeldung -->
    <div v-if="error" class="error-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ error }}</span>
    </div>
    
    <!-- Musiktitel-Tab -->
    <div v-if="activeTab === 'tracks'">
      <div class="admin-header">
        <h2>Musik verwalten</h2>
        <button @click="addNewTrack" class="btn btn-primary" :disabled="isLoading">
          <span v-if="!isLoading">Neuen Titel hinzufügen</span>
          <span v-else>Laden...</span>
        </button>
      </div>
      
      <div v-if="isLoading && !showForm" class="loading-state">
        <div class="spinner"></div>
        <p>Daten werden geladen...</p>
      </div>
      
      <div v-else-if="tracks.length === 0" class="empty-state">
        <p>Keine Musikdaten vorhanden. Fügen Sie Ihren ersten Titel hinzu.</p>
        <button @click="addNewTrack" class="btn btn-primary">Neuen Titel hinzufügen</button>
      </div>
      
      <div v-else class="tracks-list">
        <div v-for="track in tracks" :key="track.id" class="track-item">
          <div class="track-status" :class="{ published: track.isPublished, unpublished: !track.isPublished }">
            <div class="status-indicator"></div>
          </div>
          
          <div class="track-info">
            <h4>{{ track.title }}</h4>
            <p class="track-details">
              <span v-if="track.album">Album: {{ track.album }}</span>
              <span v-if="track.year">Jahr: {{ track.year }}</span>
            </p>
            <p class="track-description">{{ track.description }}</p>
            
            <div class="track-links">
              <a v-if="track.spotifyLink" :href="track.spotifyLink" target="_blank" class="link-icon spotify">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.5 17.3c-.2.3-.6.5-1 .2-2.8-1.7-6.3-2.1-10.5-1.1-.4.1-.8-.2-.9-.6-.1-.4.2-.8.6-.9 4.5-1 8.4-.6 11.6 1.3.4.2.5.7.2 1.1zm1.5-3.3c-.3.4-.8.6-1.2.3-3.2-2-8.1-2.6-11.9-1.4-.5.1-1-.1-1.2-.6-.1-.5.1-1 .6-1.2 4.4-1.3 9.8-.7 13.5 1.7.5.2.6.8.2 1.2zm.1-3.4c-3.8-2.3-10.2-2.5-13.8-1.4-.6.1-1.2-.2-1.3-.8-.2-.6.2-1.2.8-1.4 4.2-1.3 11.1-1 15.5 1.6.5.3.7 1 .4 1.6-.3.4-1 .6-1.6.4z"/>
                </svg>
              </a>
              <a v-if="track.youtubeLink" :href="track.youtubeLink" target="_blank" class="link-icon youtube">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm4.441 16.892c-2.102.144-6.784.144-8.883 0-2.276-.156-2.541-1.27-2.558-4.892.017-3.629.285-4.736 2.558-4.892 2.099-.144 6.782-.144 8.883 0 2.277.156 2.541 1.27 2.559 4.892-.018 3.629-.285 4.736-2.559 4.892zm-6.441-7.234l4.917 2.338-4.917 2.346v-4.684z"/>
                </svg>
              </a>
              <a v-if="track.appleMusicLink" :href="track.appleMusicLink" target="_blank" class="link-icon apple">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12.006 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.374-12-12-12zm3.494 14.375c0 .7-.7 1.2-1.399 1.2-.8 0-1.1-.5-1.1-1.101l-.001-.199c0-1.5-.7-2.2-2.5-2.2-1.2 0-2.1.5-2.1 1.3 0 .699.4 1.1 1.4 1.3l2.1.3c2.3.3 3.6 1.4 3.6 3.4 0 2.199-1.8 3.6-4.5 3.6-2.8 0-4.6-1.5-4.7-3.9v-.1h1.9c.1 1.399 1.2 2.1 2.8 2.1 1.399 0 2.4-.6 2.4-1.5 0-.7-.4-1.2-1.3-1.3l-2.2-.3c-2.2-.3-3.6-1.4-3.6-3.5 0-2.1 1.6-3.4 4.3-3.4 2.699 0 4.3 1.4 4.3 3.7v.1h-1.899zm-8.8-5.775l1.299-2.6h2l-1.9 2.6h-1.399zm5.6 0l1.3-2.6h2l-1.9 2.6h-1.4z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div class="track-actions">
            <button @click="togglePublishStatus(track)" class="btn-action publish" :title="track.isPublished ? 'Als unveröffentlicht markieren' : 'Veröffentlichen'">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
            </button>
            <button @click="editTrack(track)" class="btn-action edit" :disabled="isLoading">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <button @click="deleteTrack(track.id)" class="btn-action delete" :disabled="isLoading">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Formular zum Hinzufügen/Bearbeiten eines Tracks -->
      <div v-if="showForm" class="track-form">
        <div class="form-header">
          <h3>{{ editingTrack ? 'Track bearbeiten' : 'Neuen Track hinzufügen' }}</h3>
        </div>
        <form @submit.prevent="saveTrack" class="track-edit-form">
          <div class="form-grid">
            <div class="form-group">
              <label for="track-title">Titel *</label>
              <input 
                type="text" 
                id="track-title" 
                v-model="newTrack.title" 
                required
                class="form-control"
              >
            </div>
            <div class="form-group">
              <label for="track-album">Album</label>
              <input 
                type="text" 
                id="track-album" 
                v-model="newTrack.album" 
                class="form-control"
              >
            </div>
            <div class="form-group">
              <label for="track-year">Jahr</label>
              <input 
                type="text" 
                id="track-year" 
                v-model="newTrack.year" 
                class="form-control"
              >
            </div>
            <div class="form-group full-width">
              <label for="track-description">Beschreibung</label>
              <textarea 
                id="track-description" 
                v-model="newTrack.description" 
                rows="3" 
                class="form-control"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="track-spotify">Spotify Link</label>
              <input 
                type="url" 
                id="track-spotify" 
                v-model="newTrack.spotifyLink" 
                class="form-control"
                placeholder="https://open.spotify.com/track/..."
              >
            </div>
            <div class="form-group">
              <label for="track-youtube">YouTube Link</label>
              <input 
                type="url" 
                id="track-youtube" 
                v-model="newTrack.youtubeLink" 
                class="form-control"
                placeholder="https://youtube.com/watch?v=..."
              >
            </div>
            <div class="form-group">
              <label for="track-apple">Apple Music Link</label>
              <input 
                type="url" 
                id="track-apple" 
                v-model="newTrack.appleMusicLink" 
                class="form-control"
                placeholder="https://music.apple.com/album/..."
              >
            </div>
            <div class="form-group publish-option">
              <label class="checkbox-label">
                <input type="checkbox" v-model="newTrack.isPublished">
                <span>Veröffentlicht</span>
              </label>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" @click="cancelForm" class="btn btn-secondary">Abbrechen</button>
            <button type="submit" class="btn btn-primary" :disabled="isLoading">
              {{ isLoading ? 'Wird gespeichert...' : 'Speichern' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Einstellungen-Tab -->
    <div v-else-if="activeTab === 'settings'" class="settings-tab">
      <form @submit.prevent="saveMusicSettings" class="settings-form">
        <!-- Musikkategorie: Traditionell -->
        <div class="form-card">
          <h3>Musikkategorie: Traditionell</h3>
          <div class="form-group">
            <label for="traditional-title">Titel</label>
            <input 
              type="text" 
              id="traditional-title" 
              v-model="musicSettings.traditional_title" 
              class="form-control"
            >
          </div>
          <div class="form-group">
            <label for="traditional-description">Beschreibung</label>
            <textarea 
              id="traditional-description" 
              v-model="musicSettings.traditional_description" 
              rows="3" 
              class="form-control"
            ></textarea>
          </div>
        </div>
        
        <!-- Musikkategorie: Modern -->
        <div class="form-card">
          <h3>Musikkategorie: Modern</h3>
          <div class="form-group">
            <label for="modern-title">Titel</label>
            <input 
              type="text" 
              id="modern-title" 
              v-model="musicSettings.modern_title" 
              class="form-control"
            >
          </div>
          <div class="form-group">
            <label for="modern-description">Beschreibung</label>
            <textarea 
              id="modern-description" 
              v-model="musicSettings.modern_description" 
              rows="3" 
              class="form-control"
            ></textarea>
          </div>
        </div>
        
        <!-- Musikkategorie: Live -->
        <div class="form-card">
          <h3>Musikkategorie: Live</h3>
          <div class="form-group">
            <label for="live-title">Titel</label>
            <input 
              type="text" 
              id="live-title" 
              v-model="musicSettings.live_title" 
              class="form-control"
            >
          </div>
          <div class="form-group">
            <label for="live-description">Beschreibung</label>
            <textarea 
              id="live-description" 
              v-model="musicSettings.live_description" 
              rows="3" 
              class="form-control"
            ></textarea>
          </div>
        </div>
        
        <!-- Streaming-Links -->
        <div class="form-card">
          <h3>Streaming-Links</h3>
          <div class="form-group">
            <label for="spotify-url">Spotify</label>
            <input 
              type="url" 
              id="spotify-url" 
              v-model="musicSettings.spotify_url" 
              class="form-control"
              placeholder="https://open.spotify.com/artist/..."
            >
          </div>
          <div class="form-group">
            <label for="apple-music-url">Apple Music</label>
            <input 
              type="url" 
              id="apple-music-url" 
              v-model="musicSettings.apple_music_url" 
              class="form-control"
              placeholder="https://music.apple.com/artist/..."
            >
          </div>
          <div class="form-group">
            <label for="youtube-music-url">YouTube Music</label>
            <input 
              type="url" 
              id="youtube-music-url" 
              v-model="musicSettings.youtube_music_url" 
              class="form-control"
              placeholder="https://music.youtube.com/channel/..."
            >
          </div>
        </div>
        
        <!-- Aktionen -->
        <div class="form-actions">
          <button type="submit" class="btn-save" :disabled="isLoading">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            Einstellungen speichern
          </button>
        </div>
      </form>
    </div>
    
    <!-- Ladeanzeige -->
    <div v-if="isLoading" class="loading-indicator">
      <div class="spinner"></div>
      <span>Wird geladen...</span>
    </div>
  </div>
</template>

<style scoped>
.music-manager {
  padding: 1.5rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eaeaea;
}

.section-header h2 {
  margin: 0;
  color: var(--primary-color);
}

.section-header p {
  margin: 0;
  color: var(--text-muted);
}

.tab-navigation {
  display: flex;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #eee;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
}

.tab-button.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.tab-button:hover {
  color: var(--primary-color);
}

.settings-tab {
  margin-top: 1rem;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background-color: rgba(76, 175, 80, 0.1);
  color: #388e3c;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background-color: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.form-card {
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.form-card h3 {
  font-size: 1.25rem;
  margin-bottom: 1.25rem;
  color: var(--text-color);
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-save:hover {
  background-color: var(--primary-color-dark);
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: var(--text-muted);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.empty-state p {
  margin-bottom: 1.5rem;
  color: var(--text-muted);
}

.tracks-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.track-item {
  display: flex;
  align-items: center;
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
  transition: var(--transition-normal);
}

.track-item:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.track-status {
  margin-right: 1.25rem;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.published .status-indicator {
  background-color: var(--primary-color);
}

.unpublished .status-indicator {
  background-color: #ccc;
}

.track-info {
  flex: 1;
}

.track-info h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.track-details {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: var(--text-muted);
  display: flex;
  gap: 1rem;
}

.track-description {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
}

.track-links {
  display: flex;
  gap: 1rem;
}

.link-icon {
  display: inline-flex;
  color: var(--text-muted);
  transition: color 0.3s ease;
}

.link-icon.spotify:hover {
  color: #1DB954;
}

.link-icon.youtube:hover {
  color: #FF0000;
}

.link-icon.apple:hover {
  color: #fc3c44;
}

.track-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--text-muted);
  transition: var(--transition-normal);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-action.edit:hover:not(:disabled) {
  color: var(--primary-color);
  background-color: rgba(0, 132, 61, 0.1);
}

.btn-action.delete:hover:not(:disabled) {
  color: #e53935;
  background-color: rgba(229, 57, 53, 0.1);
}

.btn-action.publish:hover:not(:disabled) {
  color: #f6b60c;
  background-color: rgba(246, 182, 12, 0.1);
}

button {
  cursor: pointer;
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 500;
  border: none;
  transition: var(--transition-normal);
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-dark);
  transform: translateY(-2px);
}

.track-form {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.form-header {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.form-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.full-width {
  grid-column: 1 / -1;
}

.publish-option {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.btn-secondary {
  background-color: #eaeaea;
  color: var(--text-color);
}

.btn-secondary:hover:not(:disabled) {
  background-color: #ddd;
}
</style> 